import {
  TEL_AVIV_OFFICE,
  businessOfficeMapUrl,
  serviceByType,
  type PageOverrides,
  type ReviewItem,
  type SeoService,
} from '@/constants/core';
import {
  REGIONS,
  findRegionByCity,
  globalTypePath,
  isRegionName,
  type EventType,
  type Region,
  type SeoCity,
  typeLocationPath,
} from '@/constants/geo';
import {
  buildHomeOverrides,
  buildTargetOverrides,
} from '@/constants/buildPages/overrides';
import {
  GLOBAL_TYPE_LINK_LANGS,
  getGlobalTypeLabelForLang,
  getGlobalTypePathForLang,
  getLocalizedGlobalTypePath,
} from '@/constants/localized-routes';
import type { AppLang, I18nConstants } from '@/i18n/locales/i18n.factory';
import {
  buildGlobalServiceCityFooterLinks,
  buildInternalLinksForTarget,
  buildMainServiceParentLinks,
  type InternalLink,
} from '@/utils/internalLinks';

const PLANNING_GUIDE_PATH = '/מדריך-אירועים/';

export type RuntimePageKind =
  | 'home'
  | 'target-global'
  | 'target-region'
  | 'target-city';

export type RuntimePageConfig = {
  overrides: PageOverrides;
  internalLinks: {
    section2?: InternalLink[];
    section4?: InternalLink[];
    footer?: InternalLink[];
  };
  serviceForSection5?: SeoService;
};

const relUrlWithSlash = (pathOrUrl: string): string => {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl.endsWith('/') ? pathOrUrl : `${pathOrUrl}/`;
  }

  const clean = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return clean.endsWith('/') ? clean : `${clean}/`;
};

const asRegionMaybe = (label?: string): Region | null => {
  if (!label) return null;
  const normalized = String(label).trim();
  return isRegionName(normalized) ? (normalized as Region) : null;
};

const businessOfficeForLocation = (
  locationLabel?: string,
): typeof TEL_AVIV_OFFICE | undefined => {
  const loc = String(locationLabel ?? '').trim();
  if (!loc) return undefined;

  const region =
    asRegionMaybe(loc) ||
    (typeof findRegionByCity === 'function'
      ? (findRegionByCity(loc) as Region | null)
      : null);

  return region === 'צפון' ? TEL_AVIV_OFFICE : undefined;
};

const localizedGlobalServiceLinks = (
  i18n: I18nConstants,
  currentType: EventType,
): InternalLink[] => {
  if (i18n.HTML_LANG === 'he') return [];

  return (['אירוע', 'חתונה', 'חינה', 'בר-מצווה'] as EventType[])
    .filter(candidateType => candidateType !== currentType)
    .map(candidateType => {
      const href = getLocalizedGlobalTypePath(i18n.HTML_LANG, candidateType);
      const label = i18n.preferredKeywordForService(
        serviceByType[candidateType],
      );

      return {
        label,
        title: label,
        href: relUrlWithSlash(href ?? globalTypePath(candidateType)),
      };
    });
};

const englishGlobalServiceLinks = (): InternalLink[] => {
  return (['חתונה', 'חינה', 'בר-מצווה', 'אירוע'] as EventType[])
    .map(type => {
      const href = getGlobalTypePathForLang('en', type);
      const label = getGlobalTypeLabelForLang('en', type);

      return href
        ? {
            label,
            title: label,
            href: relUrlWithSlash(href),
          }
        : null;
    })
    .filter(Boolean) as InternalLink[];
};

const sameTypeLanguageLinks = (
  i18n: I18nConstants,
  currentType: EventType,
): InternalLink[] => {
  const currentLang = i18n.HTML_LANG as AppLang;

  return GLOBAL_TYPE_LINK_LANGS.filter(lang => lang !== currentLang)
    .map(lang => {
      const href = getGlobalTypePathForLang(lang, currentType);
      const label =
        lang === currentLang
          ? i18n.preferredKeywordForService(serviceByType[currentType])
          : getGlobalTypeLabelForLang(lang, currentType);

      return href
        ? {
            label,
            title: label,
            href: relUrlWithSlash(href),
          }
        : null;
    })
    .filter(Boolean) as InternalLink[];
};

function buildHomeRuntime(
  i18n: I18nConstants,
  siteReviews?: ReviewItem[],
): RuntimePageConfig {
  const overrides = buildHomeOverrides(i18n, {
    section6Reviews: Array.isArray(siteReviews) ? siteReviews : undefined,
    section6ShowAggregate: true,
    section5Links: [
      {
        label: i18n.PLANNING_GUIDE_TITLE,
        href: PLANNING_GUIDE_PATH,
        title: i18n.PLANNING_GUIDE_SUBTITLE,
      },
    ],
  });

  const section4Links = buildMainServiceParentLinks(i18n as any);
  const isHe = i18n.HTML_LANG === 'he';
  const footerLinks: InternalLink[] = [
    ...(['אירוע', 'חתונה', 'חינה', 'בר-מצווה'] as EventType[]).flatMap(type => {
      const keyword = i18n.preferredKeywordForService(serviceByType[type]);

      return REGIONS.map(region => {
        const regionUi = i18n.translateRegion(region);
        const inLoc = isHe ? `ב${regionUi}` : `in ${regionUi}`;
        const label = `${keyword} ${inLoc}`;

        return {
          label,
          href: relUrlWithSlash(typeLocationPath(type, region)),
          title: label,
        };
      });
    }),
    ...englishGlobalServiceLinks(),
  ];

  return {
    overrides,
    internalLinks: {
      section4: section4Links,
      footer: footerLinks,
    },
  };
}

function buildTargetRuntime(
  i18n: I18nConstants,
  type: EventType,
  locationLabel?: string,
  siteReviews?: ReviewItem[],
): RuntimePageConfig {
  const service: SeoService = serviceByType[type];
  const keyword = i18n.preferredKeywordForService(service);
  const loc = String(locationLabel ?? '').trim();
  const isGlobal = !loc;
  const isHe = i18n.HTML_LANG === 'he';
  const locUi = loc ? i18n.translateLocationLabel(loc) : '';
  const businessOffice = businessOfficeForLocation(loc);

  const overrides = buildTargetOverrides(
    service,
    keyword,
    isGlobal ? '' : locUi,
    i18n,
    {
      section6Reviews: Array.isArray(siteReviews) ? siteReviews : undefined,
      section6ShowAggregate: true,
    },
  );

  if (isGlobal) {
    overrides.section1 = overrides.section1 || {};
    const section1Title = String(overrides.section1.title || keyword).replace(
      /דיג׳יי/g,
      'די ג׳יי',
    );
    overrides.section1.title = `${section1Title} – ${i18n.BRAND_TITLE_SHORT}`;
  }

  if (businessOffice) {
    overrides.section4 = overrides.section4 || {};
    overrides.section4.navigateHref = businessOfficeMapUrl(businessOffice);
  }

  let section4Links: InternalLink[] = [];
  let footerLinks: InternalLink[] = [];

  if (isGlobal) {
    if (isHe) {
      section4Links = (REGIONS as readonly Region[]).map(region => {
        const regionUi = i18n.translateRegion(region);
        const label = `${keyword} ב${regionUi}`;

        return {
          label,
          href: relUrlWithSlash(typeLocationPath(type, region)),
          title: label,
        };
      });

      footerLinks = buildGlobalServiceCityFooterLinks(i18n as any, {
        type,
        service,
      });
    } else {
      section4Links = localizedGlobalServiceLinks(i18n, type);
      footerLinks = sameTypeLanguageLinks(i18n, type);
    }
  } else {
    const region: Region =
      asRegionMaybe(loc) ||
      (typeof findRegionByCity === 'function'
        ? (findRegionByCity(loc) as Region | null)
        : null) ||
      ('מרכז' as Region);

    const cluster = buildInternalLinksForTarget(i18n as any, {
      region,
      city: loc as SeoCity,
      service,
      type,
      maxS4: 6,
      maxFooter: 30,
      includeRegionTarget: true,
    });

    overrides.section5 = overrides.section5 || {};
    overrides.section5.links = cluster.section5;
    overrides.section5.linksHeading = isHe
      ? 'עוד על סוג האירוע'
      : 'More about this event';

    overrides.section6 = overrides.section6 || { reviews: [] };
    overrides.section6.links = cluster.section6;
    overrides.section6.linksHeading = isHe
      ? 'עוד באזור שלך'
      : 'More in your area';

    section4Links = cluster.section4;
    footerLinks = cluster.footer;
  }

  return {
    overrides,
    internalLinks: {
      section4: section4Links,
      footer: footerLinks,
      section2: [],
    },
    serviceForSection5: service,
  };
}

export function buildRuntimePageConfig(
  kind: RuntimePageKind,
  params: {
    lang?: AppLang;
    region?: Region;
    city?: SeoCity;
    type?: EventType;
    siteReviews?: ReviewItem[];
    i18n?: I18nConstants;
  } = {},
): RuntimePageConfig {
  const i18n = params.i18n;
  if (!i18n) {
    throw new Error('buildRuntimePageConfig requires an i18n instance');
  }

  if (kind === 'home') {
    return buildHomeRuntime(i18n, params.siteReviews);
  }

  if (kind === 'target-global' && params.type) {
    return buildTargetRuntime(i18n, params.type, undefined, params.siteReviews);
  }

  if (kind === 'target-region' && params.type && params.region) {
    return buildTargetRuntime(
      i18n,
      params.type,
      String(params.region),
      params.siteReviews,
    );
  }

  if (kind === 'target-city' && params.type && params.city) {
    return buildTargetRuntime(
      i18n,
      params.type,
      String(params.city),
      params.siteReviews,
    );
  }

  return buildHomeRuntime(i18n, params.siteReviews);
}
